import React from 'react';
import { Chip, Tooltip, Box, LinearProgress, Typography, Button } from '@mui/material';
import { 
  CheckCircle, 
  Schedule, 
  Cancel, 
  Warning,
  Assignment,
  Info
} from '@mui/icons-material';
import { analyzeProfileCompletion, getCompletionSummary } from '../utils/ProfileCompletionTracker';

const OnboardingStatusChip = ({ 
  maidProfile, 
  showProgress = false, 
  variant = 'chip', // 'chip' | 'detailed'
  size = 'small',
  onViewMissingFields = null // Callback to show missing fields panel
}) => {
  const status = maidProfile?.onboardingStatus || 'incomplete';
  const onboardingMeta = maidProfile?.onboardingMeta || {};
  
  // Calculate progress using new completion tracker
  const profileAnalysis = maidProfile ? analyzeProfileCompletion(maidProfile) : null;
  const completionSummary = profileAnalysis ? getCompletionSummary(profileAnalysis) : null;
  
  // Fallback to old method if new analysis fails
  const totalSteps = onboardingMeta.totalSteps || 6;
  const completedSteps = onboardingMeta.lastCompletedStep || 0;
  const progressPercentage = profileAnalysis ? profileAnalysis.overall.completionPercentage : Math.round((completedSteps / totalSteps) * 100);

  // Enhanced tooltip content with missing fields
  const getTooltipContent = () => {
    if (!profileAnalysis) {
      return {
        incomplete: `Onboarding in progress (${completedSteps}/${totalSteps} steps completed)`,
        pending_review: 'Onboarding submitted, awaiting admin review',
        approved: 'Onboarding completed and approved',
        rejected: 'Onboarding rejected, requires revision'
      }[status];
    }

    const { overall, allMissingRequired } = profileAnalysis;
    
    switch (status) {
      case 'incomplete':
        if (allMissingRequired.length === 0) {
          return `All required fields complete • ${overall.completionPercentage}% overall completion`;
        }
        const missingCount = allMissingRequired.length;
        const firstFewMissing = allMissingRequired.slice(0, 3).map(field => field.label).join(', ');
        const moreText = missingCount > 3 ? ` and ${missingCount - 3} more` : '';
        return `${missingCount} required fields missing: ${firstFewMissing}${moreText}`;
        
      case 'pending_review':
        return `All required fields complete • Pending admin review • ${overall.completionPercentage}% overall completion`;
        
      case 'approved':
        return `Profile approved and active • ${overall.completionPercentage}% completion`;
        
      case 'rejected':
        return `Profile rejected • ${allMissingRequired.length} required fields need attention`;
        
      default:
        return completionSummary?.message || 'Status unknown';
    }
  };

  // Status configuration
  const statusConfig = {
    incomplete: {
      label: 'Incomplete',
      color: 'error',
      icon: <Warning />,
      tooltip: getTooltipContent()
    },
    pending_review: {
      label: 'Pending Review',
      color: 'warning',
      icon: <Schedule />,
      tooltip: getTooltipContent()
    },
    approved: {
      label: 'Approved',
      color: 'success',
      icon: <CheckCircle />,
      tooltip: getTooltipContent()
    },
    rejected: {
      label: 'Rejected',
      color: 'error',
      icon: <Cancel />,
      tooltip: getTooltipContent()
    }
  };

  const config = statusConfig[status] || statusConfig.incomplete;

  // Format completion date
  const getCompletionDate = () => {
    const completedAt = onboardingMeta.onboardingCompletedAt || onboardingMeta.lastSavedAt;
    if (!completedAt) return null;
    return new Date(completedAt).toLocaleDateString();
  };

  if (variant === 'detailed') {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          {config.icon}
          <Typography variant="h6" component="h3">
            Onboarding Status
          </Typography>
        </Box>
        
        <Box mb={2}>
          <Chip
            label={config.label}
            color={config.color}
            icon={config.icon}
            size="medium"
            sx={{ mb: 1 }}
          />
          
          {/* Enhanced progress display */}
          <Box mt={1}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Progress: {progressPercentage}% complete
              {profileAnalysis && (
                <> • {profileAnalysis.overall.completedSections} of {profileAnalysis.overall.totalSections} sections</>
              )}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={progressPercentage} 
              color={progressPercentage > 80 ? 'success' : progressPercentage > 50 ? 'warning' : 'error'}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
          
          {/* Missing fields summary */}
          {profileAnalysis && profileAnalysis.allMissingRequired.length > 0 && (
            <Box mt={2} p={1.5} sx={{ backgroundColor: 'error.light', borderRadius: 1, border: '1px solid', borderColor: 'error.main' }}>
              <Typography variant="body2" color="error.dark" gutterBottom>
                <Warning sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                {profileAnalysis.allMissingRequired.length} required field{profileAnalysis.allMissingRequired.length === 1 ? '' : 's'} missing
              </Typography>
              <Typography variant="caption" color="error.dark">
                {profileAnalysis.allMissingRequired.slice(0, 3).map(field => field.label).join(', ')}
                {profileAnalysis.allMissingRequired.length > 3 && ` and ${profileAnalysis.allMissingRequired.length - 3} more`}
              </Typography>
            </Box>
          )}
          
          {/* View missing fields button */}
          {profileAnalysis && profileAnalysis.allMissingRequired.length > 0 && onViewMissingFields && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Info />}
              onClick={() => onViewMissingFields(profileAnalysis)}
              sx={{ mt: 1 }}
            >
              View Missing Fields
            </Button>
          )}
          
          {getCompletionDate() && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {status === 'approved' ? 'Completed: ' : 'Last Updated: '}{getCompletionDate()}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  // Default chip variant
  const chipElement = (
    <Chip
      label={status === 'incomplete' && showProgress ? `${config.label} (${progressPercentage}%)` : config.label}
      color={config.color}
      icon={config.icon}
      size={size}
      variant="outlined"
      sx={{
        fontWeight: 500,
        ...(status === 'incomplete' && {
          borderStyle: 'dashed'
        })
      }}
    />
  );

  return (
    <Tooltip title={config.tooltip} arrow>
      {chipElement}
    </Tooltip>
  );
};

export default OnboardingStatusChip;